import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Toaster, ToasterType } from 'components/Toaster';

const DEFAULT_TIMEOUT = 3000;
/** Must match the leave duration on the Toaster transition, so the toast is removed only once it has animated out. */
const LEAVE_DURATION = 500;

let toastCount = 0;
const nextToastId = () => `toast-${(toastCount += 1)}`;

export type ToastOptions = {
  message?: string;
  timeout?: number;
};

type ToastState = {
  id: string;
  type: ToasterType;
  title: string;
  message?: string;
  timeout: number;
  isVisible: boolean;
};

type ShowToast = (type: ToasterType, title: string, options?: ToastOptions) => string;
type ShowToastOfType = (title: string, options?: ToastOptions) => string;

type ToastContextValue = {
  showToast: ShowToast;
  success: ShowToastOfType;
  error: ShowToastOfType;
  warning: ShowToastOfType;
  info: ShowToastOfType;
  closeToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

type ToastItemProps = {
  toast: ToastState;
  onHide: (id: string) => void;
  onRemove: (id: string) => void;
};

const ToastItem = ({ toast, onHide, onRemove }: ToastItemProps) => {
  const { id, isVisible, timeout } = toast;

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const timer = setTimeout(() => onHide(id), timeout);
    return () => clearTimeout(timer);
  }, [id, isVisible, timeout, onHide]);

  useEffect(() => {
    if (isVisible) {
      return;
    }
    const timer = setTimeout(() => onRemove(id), LEAVE_DURATION);
    return () => clearTimeout(timer);
  }, [id, isVisible, onRemove]);

  return (
    <Toaster
      id={id}
      isVisible={isVisible}
      type={toast.type}
      title={toast.title}
      message={toast.message}
      onClose={() => onHide(id)}
    />
  );
};

type Props = {
  children: React.ReactNode;
};

export const ToastProvider = ({ children }: Props): JSX.Element => {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts((current) =>
      current.map((toast) => (toast.id === id ? { ...toast, isVisible: false } : toast))
    );
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback<ShowToast>((type, title, options) => {
    const id = nextToastId();
    setToasts((current) => [
      ...current,
      {
        id,
        type,
        title,
        message: options?.message,
        timeout: options?.timeout ?? DEFAULT_TIMEOUT,
        isVisible: true,
      },
    ]);
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      showToast,
      success: (title, options) => showToast(ToasterType.Success, title, options),
      error: (title, options) => showToast(ToasterType.Error, title, options),
      warning: (title, options) => showToast(ToasterType.Warning, title, options),
      info: (title, options) => showToast(ToasterType.Info, title, options),
      closeToast: hideToast,
    }),
    [showToast, hideToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-6 right-6 z-[300] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onHide={hideToast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};
